import { HttpClient, HttpContext } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ApolloLink } from '@apollo/client/core';
import { environment } from '@env/environment';
import { Apollo, ApolloBase, gql } from 'apollo-angular';
import { ListingsService } from 'listings/services/listings.service';
import camelCase from 'lodash-es/camelCase';
import { BehaviorSubject, Observable, Subject, Subscription, combineLatest, exhaustMap, filter, map, of, takeWhile } from 'rxjs';
import { BypassErrorLink } from 'shared/abstract/bypass-error-link';
import { IS_SHIJI_USER_KEY, PASSWORD_CHANGE_TOKEN } from 'shared/constants/general-constants';
import { BYPASS_ERROR_HANDLER_FOR_ERROR_CODES } from 'shared/interceptors/error.interceptor';
import { ChangePassword } from 'shared/interfaces/change-password.interface';
import { CurrentShijiUserInterface } from 'shared/interfaces/current-shiji-user.interface';
import { CurrentSupplierUserInterface, isCurrentSupplierUser } from 'shared/interfaces/current-supplier-user.interface';
import { FeatureGroup } from 'shared/interfaces/feature-group.interface';
import { ResetPassword } from 'shared/interfaces/reset-password.interface';
import { UserCredentials } from 'shared/interfaces/user-credentials.interface';
import { UserInviteInfo } from 'shared/interfaces/user-invite.interface';
import { StorageService } from 'shared/services/index';
import { unsubscribeFromAll } from 'shared/util/unsubscribeFromAll';
import { CreateShijiUser, CreateSupplierUser } from 'users/interfaces/create-user.interface';
import { SuppliersInviteGroup } from 'users/interfaces/suppliers-invite-group.interface';
import { VisualsPermissionsService } from 'visuals/services/visuals-permissions.service';

const user_invite_urls = {
    getInviteData: environment.BASE_URL + 'userApi/invitation/get-invite-data?inviteId=',
    getInviteContext: environment.BASE_URL + 'userApi/invitation/get-invite-context',
    getAllSuppliersInviteInformation: environment.BASE_URL + 'userApi/invitation/getAllSuppliersInviteInformation',
    getSingleSupplierInviteInformation: environment.BASE_URL + 'userApi/invitation/getAllSupplierInviteInformation?supplierId=',
    createUserInvite: environment.BASE_URL + 'userApi/invitation/create-user-invite',
    createShijiInvite: environment.BASE_URL + 'userApi/invitation/create-shiji-invite',
    acceptInvite: environment.BASE_URL + 'userApi/invitation/acceptInvite',
};
const user_request_urls = {
    getData: environment.BASE_URL + 'userApi/request/get-data',
    addRequest: environment.BASE_URL + 'userApi/request/add-request',
    acceptForgotPassword: environment.BASE_URL + 'userApi/request/accept-forgot-password',
    changePassword: environment.BASE_URL + 'userApi/request/change-password',
    acceptRequestedPasswordChange: environment.BASE_URL + 'userApi/request/accept-requested-password-change',
};
const shijiUserVariations = ['SHIJIIDENTITY', 'SHIJIUSER', 'SHIJI_USER', 'SHIJI'];

const USER_API = environment.BASE_URL + 'userApi/';

@Injectable({
    providedIn: 'root',
})
export class UserApiService {
    public currentUser?: CurrentSupplierUserInterface | CurrentShijiUserInterface;

    public set isShiji(value: boolean) {
        this._isShiji = value;
    }

    public get isShiji() {
        return this._isShiji;
    }

    private _isShiji = false;
    private apollo: ApolloBase;
    private featureGroups?: FeatureGroup[];
    private subscriptions: { [key: string]: Subscription | undefined } = {};
    private resources: BehaviorSubject<any> = new BehaviorSubject(undefined);
    private loginInformation: BehaviorSubject<any> = new BehaviorSubject(undefined);

    constructor(
        private http: HttpClient,
        private apolloProvider: Apollo,
        private listingsService: ListingsService,
        private storageService: StorageService,
        private visualsPermissionService: VisualsPermissionsService,
    ) {
        this.apollo = this.apolloProvider.use('usersAPI');
    }

    //User Invite
    public getUserInviteData(invitationId: string): Observable<UserInviteInfo> {
        return this.http.get<UserInviteInfo>(user_invite_urls.getInviteData + invitationId, {
            context: new HttpContext().set(BYPASS_ERROR_HANDLER_FOR_ERROR_CODES, [404, 412]),
        });
    }

    public getInviteContext(inviteId: string, email: string): Observable<{ token: string }> {
        return this.http.post<{ token: string }>(
            user_invite_urls.getInviteContext,
            { inviteId, email },
            { headers: { bypassTokenRefreshInterceptor: 'true' } },
        );
    }

    public acceptInvite(userCredentials: UserCredentials, inviteContextToken: string): Observable<any> {
        return this.http.post<UserCredentials>(user_invite_urls.acceptInvite, userCredentials, {
            context: new HttpContext().set(BYPASS_ERROR_HANDLER_FOR_ERROR_CODES, [409]),
            headers: {
                Authorization: 'Bearer ' + inviteContextToken,
                bypassTokenRefreshInterceptor: 'true',
            },
        });
    }

    public getAllSuppliersGroupsAndContact(): Observable<SuppliersInviteGroup[]> {
        return this.http.get(user_invite_urls.getAllSuppliersInviteInformation).pipe(
            map((info: any) => {
                info.suppliersData.forEach((supplier: any) => {
                    const supplierId = Object.keys(info.userGroupsData).find((key: any) => key === supplier.supplierId);
                    supplier.userGroups = supplierId ? info.userGroupsData[supplierId] : null;
                    supplier.accessGroups = supplierId ? info.listingAccessGroups[supplierId] : null;
                });
                delete info.userGroupsData;

                return info.suppliersData;
            }),
        );
    }

    public getSingleSupplierGroupsAndContact(supplierId: string): Observable<any> {
        return this.http.get(user_invite_urls.getSingleSupplierInviteInformation + supplierId).pipe(
            map((info: any) => {
                const supplier = info.suppliersData[0];
                supplier.userGroups = info.userGroupsData[supplierId];
                supplier.accessGroups = info.listingAccessGroups[supplierId];

                return supplier;
            }),
        );
    }

    public createSupplierUserInvitation(user: CreateSupplierUser): Observable<any> {
        return this.http.post(user_invite_urls.createUserInvite, user, {
            context: new HttpContext().set(BYPASS_ERROR_HANDLER_FOR_ERROR_CODES, [409]),
        });
    }

    public createShijiUserInvitation(user: CreateShijiUser): Observable<any> {
        return this.http.post(user_invite_urls.createShijiInvite, user, {
            context: new HttpContext().set(BYPASS_ERROR_HANDLER_FOR_ERROR_CODES, [409]),
        });
    }

    //User Request
    public getRequestData(requestId: string): Observable<any> {
        return this.http.get(user_request_urls.getData, { params: { requestId: requestId } });
    }

    public addRequest(email: string, identityRequestType: number): Observable<any> {
        return this.http.post(user_request_urls.addRequest, { email, identityRequestType });
    }

    public resetPassword(resetPasswordBody: ResetPassword): Observable<any> {
        return this.http.post(user_request_urls.acceptForgotPassword, resetPasswordBody);
    }

    public changePassword(changePasswordBody: ChangePassword): Observable<any> {
        return this.http.post(user_request_urls.changePassword, changePasswordBody, {
            context: new HttpContext().set(BYPASS_ERROR_HANDLER_FOR_ERROR_CODES, [400]),
        });
    }

    public changedPasswordUponRequest(password: string, requestId?: string): Observable<any> {
        const requestURL = user_request_urls.acceptRequestedPasswordChange + (requestId ? `?requestId=${requestId}` : '');
        let headers = {};
        const passwordChangeToken = this.storageService.getFromSession(PASSWORD_CHANGE_TOKEN);

        if (passwordChangeToken) {
            headers = { Authorization: 'Bearer ' + passwordChangeToken };
        }

        return this.http.post(requestURL, { password: password }, { headers: headers });
    }

    //Additional methods
    public determineIfShiji(userType: string): boolean {
        this.isShiji = shijiUserVariations.includes(userType?.toUpperCase());
        return this.isShiji;
    }

    public getCurrentUser() {
        const requestHasFinished = new Subject();

        this.subscriptions.getClaimsRequestFunction = this.getCurrentUserData()
            .pipe(
                exhaustMap((userData: CurrentSupplierUserInterface | CurrentShijiUserInterface) => {
                    const observables: Observable<any>[] = [];
                    observables.push(of(userData));

                    if (userData && !this.determineIfShiji(userData.userType)) {
                        const supplierUser = userData as CurrentSupplierUserInterface;
                        observables.push(this.listingsService.getFeatureGroupsResources(supplierUser.supplierId));
                    }

                    return combineLatest(observables);
                }),
                exhaustMap(([currentUserData, resources]) => {
                    this.featureGroups = resources = resources?.map((fg: FeatureGroup) => {
                        fg.fieldName = camelCase(fg.name?.replace('&', 'And'));
                        return fg;
                    });
                    this.resources.next(resources);

                    return of(currentUserData);
                }),
            )
            .subscribe((currentUserData) => {
                if (currentUserData?.email && currentUserData?.userType) {
                    this.currentUser = currentUserData as CurrentSupplierUserInterface | CurrentShijiUserInterface;
                    this.currentUser.isShiji = this.isShiji;
                }

                if (this.isShiji) {
                    this.visualsPermissionService.loggedInUserHasVisualsUIEnabled.set(true);
                    this.visualsPermissionService.setAdminPermissions();
                } else {
                    if (isCurrentSupplierUser(currentUserData) && currentUserData.visualPermissions) {
                        this.visualsPermissionService.setPermissions(currentUserData.visualPermissions);
                        this.visualsPermissionService.loggedInUserHasVisualsUIEnabled.set(!!currentUserData.hasVisualsUIEnabled);
                    }
                }

                currentUserData.isShiji = this.isShiji;
                this.loginInformation.next(currentUserData);
                requestHasFinished.next(true);
                requestHasFinished.complete();
            });

        return requestHasFinished;
    }

    public getCurrentUserData(): Observable<CurrentSupplierUserInterface | CurrentShijiUserInterface> {
        this.isShiji = this.storageService.getFromLocal(IS_SHIJI_USER_KEY) as boolean;
        const userType = this.isShiji ? 'Shiji' : 'Supplier';

        const query = `query get${userType}CurrentData {
            ${userType.toLowerCase()}Users {
                currentData {
                    data {
                        id
                        email
                        firstName
                        lastName
                        isDeleted
                        isEnabled
                        passwordChangeRequired
                        lastLogin
                        status
                        userType
                        idleTime
                        forceLogoutTime
                        notificationSettings {
                            id
                            isActive
                            notificationType
                            owner
                        }
                        ${
                            this.isShiji
                                ? `shijiPermissions {
                                id
                                isAuthorized
                                shijiAccessZone
                            }`
                                : `supplierId
                            companyName
                            hasPreviousUserGroup
                            isIceportalSSO
                            isDalSSO
                            hasAccessToIce
                            hasAccessToDAL
                            autoShorteningEnabled
                            autoTranslationEnabled
                            imageEditingEnabled
                            listingAccessGroups {
                                id
                                name
                                description
                                supplierId
                                companyName
                                listingAccesses {
                                    id
                                    accessIdValue
                                    accessType
                                    displayName
                                }
                            }
                            internalPermissions {
                                id
                                accessType
                                permissionOwner
                                permissionAccessZone
                            }
                            resourcePermissions {
                                id
                                resourceId
                                accessType
                                userGroupId
                            }
                            hasVisualsUIEnabled
                            visualPermissions {
                                    id
                                    userId
                                    userGroupId
                                    type
                                    accessType
                                    isImplemented
                                    derivedVisualPermissions {
                                        visualPermissionId
                                        type
                                        isActive
                                        isImplemented
                                        id
                                    }
                            }
                            userAccesses {
                                userGroupId
                            }`
                        }
                    }
                    statusCode
                    message
                }
            }
        }`;

        return this.apollo
            .watchQuery({
                query: gql(query),
                fetchPolicy: 'no-cache',
            })
            .valueChanges.pipe(
                map((response: any) => {
                    return {
                        ...response.data[`${userType.toLowerCase()}Users`].currentData.data,
                        isShiji: this.isShiji,
                    };
                }),
            );
    }

    public getLoginInformation(): Observable<CurrentSupplierUserInterface | CurrentShijiUserInterface> {
        return this.loginInformation.asObservable().pipe(
            takeWhile((value) => value === undefined, true),
            filter((value) => value !== undefined),
        ) as Observable<CurrentSupplierUserInterface | CurrentShijiUserInterface>;
    }

    public getLoginInformationContinuously(): Observable<CurrentSupplierUserInterface | CurrentShijiUserInterface> {
        return this.loginInformation.asObservable().pipe(filter((value) => value !== undefined)) as Observable<
            CurrentSupplierUserInterface | CurrentShijiUserInterface
        >;
    }

    public getResourcesInformation(): Observable<FeatureGroup[]> {
        return this.resources.asObservable() as Observable<FeatureGroup[]>;
    }

    public removeCurrentUser() {
        this.currentUser = undefined;
        this.loginInformation.next(undefined);
        unsubscribeFromAll(this.subscriptions);
    }

    public getUserToken(isShijiUser: boolean, redirectToLoginPageOnError: boolean = false): Observable<any> {
        const currentUserType = isShijiUser ? 'Shiji' : 'Supplier';

        const query = `query getCurrent${currentUserType}User {
            ${currentUserType.toLowerCase()}Users {
                current${currentUserType}User {
                    data
                    statusCode
                    message
                }
            }
        }`;

        let context: any = undefined;

        if (redirectToLoginPageOnError) {
            context = {
                headers: {
                    redirectToLoginPageForCodes: [401, 456],
                },
                link: ApolloLink.from([new BypassErrorLink([401, 456])]),
            };
        }

        // This *MUST* use query instead of watchQuery, otherwise the token refresh will never complete leaving certain calls hanging
        return this.apollo
            .query({
                query: gql(query),
                fetchPolicy: 'no-cache',
                context: context,
            })
            .pipe(
                map(({ data }: { data: any }) => {
                    return data[`${currentUserType.toLowerCase()}Users`][`current${currentUserType}User`].data;
                }),
            );
    }

    public getCurrentSupplierUserData(): Observable<CurrentSupplierUserInterface> {
        return this.apollo
            .query({
                query: gql`
                    query getUserProfileInformation {
                        supplierUsers {
                            currentProfileData {
                                data {
                                    id
                                    firstName
                                    lastName
                                    email
                                    companyName
                                    userGroupName
                                    notificationSettings {
                                        id
                                        isActive
                                        notificationType
                                        owner
                                    }
                                }
                            }
                        }
                    }
                `,
                fetchPolicy: 'no-cache',
            })
            .pipe(
                map((response: any) => {
                    return {
                        ...response.data.supplierUsers.currentProfileData.data,
                        isShiji: false,
                    };
                }),
            );
    }

    public redirectToDal(): Observable<{ dalRedirectUrl: string }> {
        return this.http.get<{ dalRedirectUrl: string }>(USER_API + 'external-redirect/dal-redirect', {});
    }
}
